// Base
import Link from 'next/link'
import { UrlObject } from 'url'

// Components
import Text from '@components/helpers/text/Text'

// Styles
import styles from './Button.module.sass'
import LoadingSpinner from '@components/helpers/loadingSpinner/LoadingSpinner'

interface ButtonBase {
  variant?:
    | 'button-1'
    | 'button-2'
    | 'button-3'
    | 'button-4'
    | 'provider'
    | 'custom'
  buttonColor?: string
  opacity?: number
  buttonSize?: 'xl' | 'lg' | 'md' | 'sm'
  buttonWidth?: number
  borderRadius?: 'sm' | 'md'
  border?: 'white' | 'pink' | 'grey'
  textColor?: 'white' | 'pink' | 'black'
  textSize?: string
  hint?: string
  shadow?: boolean
  loading?: boolean
  disabled?: boolean
  startIcon?: JSX.Element
  endIcon?: JSX.Element
  className?: string
  children?: string
  external?: boolean
  transform?: 'uppercase'
}

interface ButtonProps extends ButtonBase {
  href?: UrlObject | string
  onClick?: Function
}

const Button = ({
  variant = 'custom',
  buttonColor = 'pink',
  opacity = 100,
  children,
  buttonSize = 'md',
  buttonWidth,
  borderRadius = 'md',
  border,
  shadow = false,
  textColor = 'white',
  textSize = 'body-2',
  hint,
  loading = false,
  disabled = false,
  startIcon,
  endIcon,
  className = '',
  external,
  href,
  transform,
  onClick
}: ButtonProps) => {
  const handleClick = () => {
    if (!disabled && onClick) {
      onClick()
      return
    }
  }

  const fontSize = () => {
    switch (variant) {
      case 'button-2':
        return 'body-1'
      case 'button-3':
        return 'body-1'
      case 'provider':
        return 'body-1'
      case 'button-4':
        return 'body-7'
      default:
        return textSize
    }
  }

  return (
    <button
      style={{ width: buttonWidth ? `${buttonWidth}px` : '100%' }}
      className={`
        ${styles.button}
        ${styles[variant]}
        ${styles[`${buttonColor}-${opacity}`]}
        ${styles[`buttonSize-${buttonSize}`]}
        ${styles[`borderRadius-${borderRadius}`]}
        ${border ? styles[`border-${border}`] : ''}
        ${shadow ? styles[`shadow-${shadow}`] : ''}
        ${loading ? styles['loading'] : ''}
        ${transform ? styles[transform] : ''} 
        ${className}
      `}
      disabled={disabled}
      onClick={handleClick}
    >
      <LinkWrapper href={href} target={external ? '_blank' : '_self'}>
        {startIcon}

        <div>
          <Text
            tagName="p"
            variant={fontSize()}
            color={textColor}
            fontWeight={700}
          >
            {children}
          </Text>
          {hint && (
            <Text
              tagName="p"
              variant="body-3"
              color={textColor}
              className={styles.hint}
            >
              {hint}
            </Text>
          )}
        </div>

        {endIcon}

        {loading && <LoadingSpinner size="xs" color="black" />}
      </LinkWrapper>
    </button>
  )
}

const LinkWrapper = ({
  href,
  target,
  children
}: {
  href?: UrlObject | string
  target?: '_self' | '_blank'
  children: React.ReactNode
}) => {
  if (href) {
    return (
      <Link href={href} target={target} className={styles.buttonLink}>
        {children}
      </Link>
    )
  }
  return <div className={styles.buttonLink}>{children}</div>
}

export { Button }
